<template>
  <div>
    <h3>
      <strong>{{ route.meta.title }}</strong>
    </h3>
    <a-card class="mt-4">
      <div class="d-flex justify-content-end mb-4">
        <a-select
          v-model:value="selectedLevels"
          mode="multiple"
          placeholder="Filter Level"
          style="width: 200px; margin-right: 8px;"
          :options="levelOptions"
          allow-clear
        />
        <a-select
          v-model:value="selectedBranches"
          mode="multiple"
          placeholder="Filter per Cabang"
          style="width: 200px;"
          :options="branchOptions"
          allow-clear
        />
      </div>
      <div class="pathway-layout">
        <div v-for="(level, idx) in levelsToShow" :key="level" class="level-container">
          <div class="level-header" v-if="getPotentialParents(level).length">
            <span class="level-label">Level {{ level }}</span>

            <a-tooltip placement="right" v-if="!idx">
              <template #title>
                <strong>Information</strong> <br />
                <div style="margin-bottom: 4px; display: flex; align-items: flex-start;">
                  <span style="margin-right: 8px;">•</span>
                  <div style="flex: 1;">
                    A level is a tier in the decision tree. Within a level, you can add
                    classifications.
                  </div>
                </div>

                <div style="margin-bottom: 4px; display: flex; align-items: flex-start;">
                  <span style="margin-right: 8px;">•</span>
                  <div style="flex: 1;">
                    A new level will be created automatically if you do not check "<i
                      >mark as final classification</i
                    >" in the add classification feature.
                  </div>
                </div>

                <div style="margin-bottom: 4px; display: flex; align-items: flex-start;">
                  <span style="margin-right: 8px;">•</span>
                  <div style="flex: 1;">
                    If any classification within a level is left unchecked, the next level will be
                    created automatically.
                  </div>
                </div>
              </template>
              <InfoCircleOutlined />
            </a-tooltip>
          </div>

          <div class="level-body">
            <div
              v-for="parent in getPotentialParents(level)"
              :key="parent.id"
              class="lineage-group"
            >
              <div class="lineage-parent">
                <div v-if="parent.id !== 'root'" class="lineage-path">
                  <template
                    v-for="(ancestor, index) in getLineage(parent.id).concat(parent)"
                    :key="ancestor.id"
                  >
                    <div class="item-badge" :class="getNodeColorClass(ancestor)">
                      {{ ancestor.code }}
                    </div>

                    <div class="connector mini-arrow" v-if="index < getLineage(parent.id).length">
                      <i class="fe fe-arrow-right"></i>
                    </div>
                  </template>
                </div>

                <!-- Dynamic tooltip for lineage path -->
                <a-tooltip v-if="parent.id !== 'root'" placement="right">
                  <template #title>
                    <div>
                      <strong>Information</strong> <br />
                      <div style="margin-top: 8px;">
                        <template
                          v-for="ancestor in getLineage(parent.id).concat(parent)"
                          :key="ancestor.id"
                        >
                          <div style="margin-bottom: 4px; display: flex; align-items: flex-start;">
                            <span style="margin-right: 8px;">•</span>
                            <div style="flex: 1;">
                              <strong>{{ ancestor.code }}:</strong> {{ ancestor.name }}
                              <div
                                v-if="ancestor.detailInformation"
                                style="font-size: 12px; color: #666; margin-top: 2px;"
                              >
                                {{ ancestor.detailInformation }}
                              </div>
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </template>
                  <InfoCircleOutlined style="margin-left: 8px;" />
                </a-tooltip>
              </div>

              <div class="lineage-children">
                <div
                  v-for="child in getChildrenOf(parent.id, level)"
                  :key="child.id"
                  class="pathway-group"
                >
                  <div
                    class="pathway-item"
                    :class="getNodeColorClass(child)"
                    @click="openEditModal(child)"
                  >
                    <span class="item-badge" :class="getNodeColorClass(child)">{{
                      child.code
                    }}</span>
                    <span class="item-name">{{ child.name }}</span>
                  </div>
                </div>
                <a-button
                  v-if="shouldShowAddButton(parent)"
                  type="dashed"
                  size="small"
                  shape="circle"
                  class="add-classification-btn"
                  @click="openAddModal(parent)"
                >
                  <i class="fe fe-plus"></i>
                </a-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-card>

    <a-modal
      :visible="isModalVisible"
      :title="modalMode === 'add' ? 'Add Classification' : 'Update Classification'"
      width="600px"
      :footer="null"
      :closable="true"
      :mask-closable="false"
      @cancel="handleCancel"
      centered
    >
      <div class="lineage-path modal-lineage" v-if="modalContext.lineage.length > 0">
        <template v-for="(p, index) in modalContext.lineage" :key="index">
          <div class="item-badge" :class="getNodeColorClass(p)">
            {{ p.code }}
          </div>
          <div class="connector mini-arrow" v-if="index < modalContext.lineage.length - 1">
            <i class="fe fe-arrow-right"></i>
          </div>
        </template>
      </div>

      <a-form ref="formRef" :model="formState" layout="vertical">
        <a-form-item
          label="Classification Name"
          name="name"
          :rules="[{ required: true, message: 'Please input the classification name!' }]"
        >
          <a-input v-model:value="formState.name" placeholder="Enter classification name" />
        </a-form-item>
        <a-form-item
          label="Code"
          name="code"
          :rules="[
            { required: true, message: 'Please input the classification code!' },
            { max: 3, message: 'Code cannot have more than 3 characters!' },
          ]"
        >
          <a-input v-model:value="formState.code" placeholder="Enter classification code" />
        </a-form-item>
        <a-form-item label="Information Detail">
          <a-textarea
            v-model:value="formState.detailInformation"
            :rows="4"
            placeholder="Enter detailed information about this classification"
          />
        </a-form-item>
        <a-form-item label="Pathway Image">
          <a-upload
            v-model:file-list="imageFileList"
            name="pathwayImage"
            :max-count="1"
            :before-upload="beforeUpload"
            accept="image/*"
            list-type="picture-card"
            @preview="handlePreview"
            @change="handleUploadChange"
          >
            <div v-if="imageFileList.length < 1">
              <UploadOutlined />
              <div style="margin-top: 8px">Upload Image</div>
            </div>
          </a-upload>
          <a-modal
            :visible="previewVisible"
            :title="previewTitle"
            :footer="null"
            @cancel="handlePreviewCancel"
          >
            <img alt="preview" style="width: 100%" :src="previewImage" />
          </a-modal>
        </a-form-item>
        <a-form-item>
          <a-checkbox
            v-model:checked="formState.isFinal"
            :disabled="shouldForceFinalClassification"
          >
            Mark as final classification
          </a-checkbox>
        </a-form-item>

        <template v-if="formState.isFinal">
          <a-divider />
          <a-form-item label="Non Clickable">
            <a-input
              v-model:value="formState.nonClickable"
              placeholder="Enter non-clickable information"
            />
          </a-form-item>
          <a-form-item label="URL to Study">
            <a-input
              v-model:value="formState.urlToStudy"
              placeholder="Enter study URL (e.g., https://example.com/study)"
            />
          </a-form-item>
          <a-form-item
            label="Add Drugs"
            name="drugIds"
            :rules="[
              {
                required: true,
                message: 'Please select at least one drug!',
                type: 'array',
                min: 1,
              },
            ]"
          >
            <a-select
              v-model:value="formState.drugIds"
              mode="multiple"
              style="width: 100%"
              placeholder="Search drugs"
              show-search
              :show-arrow="true"
              :options="allDrugs.map(drug => ({ value: drug.id, label: drug.name }))"
              :filter-option="
                (input, option) => option.label.toLowerCase().includes(input.toLowerCase())
              "
            >
              <template #suffixIcon>
                <SearchOutlined />
              </template>
            </a-select>
          </a-form-item>
        </template>
      </a-form>

      <div class="modal-footer">
        <a-button
          @click="handleCancel"
          size="large"
          style=" color: #4C749F; border-radius: 4px; width: 84px;"
          >Cancel</a-button
        >
        <a-button
          v-if="modalMode === 'edit'"
          style="background-color: #F9C6C6; color: #9F4351; border-radius: 4px; width: 84px;"
          size="large"
          @click="handleToggleDelete"
          >Delete</a-button
        >
        <a-button
          style="background-color: #436894; color: white; border-radius: 4px; width: 84px;"
          size="large"
          @click="handleSave"
          >{{ modalMode === 'add' ? 'Save' : 'Update' }}</a-button
        >
      </div>
    </a-modal>
    <a-modal
      v-model:visible="toggleDelete"
      title="Delete Classification"
      @ok="handleDelete"
      centered
    >
      Are you sure you want to delete this classification? This action will also delete all
      classifications that branch from it and cannot be undone.
    </a-modal>

    <!-- Cancel Confirmation Modal -->
    <a-modal
      v-model:visible="cancelConfirmVisible"
      title="Unsaved Changes"
      @ok="confirmCancel"
      @cancel="cancelConfirmVisible = false"
      centered
    >
      <div style="display: flex; align-items: center; margin-bottom: 16px;">
        <span>You have unsaved changes. Are you sure you want to close without saving?</span>
      </div>
      <p>
        All changes will be lost if you continue.
      </p>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { Modal, notification } from 'ant-design-vue'
import { InfoCircleOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons-vue'
import { PathwayAPI, DrugAPI } from '../../../services/axios/istry/pathwayService'

const route = useRoute()

const isModalVisible = ref(false)
const formRef = ref()
const imageFileList = ref([]) // For pathway image upload
const previewVisible = ref(false)
const previewImage = ref('')
const previewTitle = ref('')
const toggleDelete = ref(false)
const modalMode = ref('add')
const editingNodeId = ref(null)
const modalContext = ref({ parentId: null, level: 1, lineage: [] })
const pathwayData = ref({
  data: [
    {
      id: 1,
      name: 'Chemotherapy',
      code: 'CT',
      level: 1,
      relatedPathId: null,
      isFinal: false,
      Drugs: [],
    },
    {
      id: 16,
      name: 'Hormone Therapy',
      code: 'HT',
      level: 1,
      relatedPathId: null,
      isFinal: false,
      Drugs: [],
    },
    {
      id: 2,
      name: 'HER2 Positive',
      code: 'HP',
      level: 2,
      relatedPathId: 1,
      isFinal: false,
      Drugs: [],
    },
    {
      id: 3,
      name: 'HER2 Negative',
      code: 'HN',
      level: 2,
      relatedPathId: 1,
      isFinal: false,
      Drugs: [],
    },
    {
      id: 4,
      name: 'Pramenopause',
      code: 'P',
      level: 2,
      relatedPathId: 16,
      isFinal: true,
      Drugs: [{ id: 3, name: 'Paclitaxel' }],
    },
    {
      id: 5,
      name: 'Early Breast Cancer',
      code: 'EB',
      level: 3,
      relatedPathId: 2,
      isFinal: false,
      Drugs: [],
    },
  ],
})
const allDrugs = ref([
  { id: 1, name: 'Docetaxel' },
  { id: 2, name: 'Cyclophosphamide' },
  { id: 3, name: 'Paclitaxel' },
  { id: 4, name: 'Doxorubicin' },
])
const initialFormState = {
  name: '',
  code: '',
  detailInformation: '',
  isFinal: false,
  nonClickable: '',
  urlToStudy: '',
  drugIds: [],
}
let formState = reactive({ ...initialFormState })
const originalFormState = ref({ ...initialFormState })
const cancelConfirmVisible = ref(false)

// Track if form has changes
const hasFormChanges = computed(() => {
  const currentFormData = {
    name: formState.name,
    code: formState.code,
    detailInformation: formState.detailInformation,
    isFinal: formState.isFinal,
    nonClickable: formState.nonClickable,
    urlToStudy: formState.urlToStudy,
    drugIds: [...formState.drugIds],
  }

  const originalFormData = {
    name: originalFormState.value.name || '',
    code: originalFormState.value.code || '',
    detailInformation: originalFormState.value.detailInformation || '',
    isFinal: originalFormState.value.isFinal || false,
    nonClickable: originalFormState.value.nonClickable || '',
    urlToStudy: originalFormState.value.urlToStudy || '',
    drugIds: [...(originalFormState.value.drugIds || [])],
  }

  // Check if all form fields except isFinal are empty
  const allFieldsEmpty =
    !currentFormData.name &&
    !currentFormData.code &&
    !currentFormData.detailInformation &&
    !currentFormData.nonClickable &&
    !currentFormData.urlToStudy &&
    currentFormData.drugIds.length === 0

  // Check if only isFinal changed from false to true
  const onlyIsFinalChangedToTrue =
    allFieldsEmpty &&
    !originalFormData.isFinal &&
    currentFormData.isFinal &&
    currentFormData.name === originalFormData.name &&
    currentFormData.code === originalFormData.code &&
    currentFormData.detailInformation === originalFormData.detailInformation &&
    currentFormData.nonClickable === originalFormData.nonClickable &&
    currentFormData.urlToStudy === originalFormData.urlToStudy &&
    JSON.stringify(currentFormData.drugIds) === JSON.stringify(originalFormData.drugIds)

  // If only isFinal changed to true with empty fields, don't consider it a meaningful change
  if (onlyIsFinalChangedToTrue) {
    // Still check for file changes
    const currentFiles = imageFileList.value.map(file => ({
      name: file.name,
      status: file.status,
      uid: file.uid,
    }))

    const originalFiles = (originalFormState.value.imageFileList || []).map(file => ({
      name: file.name,
      status: file.status,
      uid: file.uid,
    }))

    const filesChanged = JSON.stringify(currentFiles) !== JSON.stringify(originalFiles)
    return filesChanged
  }

  const formChanged = JSON.stringify(currentFormData) !== JSON.stringify(originalFormData)

  // Check file list changes - compare the actual file names/status
  const currentFiles = imageFileList.value.map(file => ({
    name: file.name,
    status: file.status,
    uid: file.uid,
  }))

  const originalFiles = (originalFormState.value.imageFileList || []).map(file => ({
    name: file.name,
    status: file.status,
    uid: file.uid,
  }))

  const filesChanged = JSON.stringify(currentFiles) !== JSON.stringify(originalFiles)

  return formChanged || filesChanged
})

const branchColors = [
  'branch-color-1',
  'branch-color-2',
  'branch-color-3',
  'branch-color-4',
  'branch-color-5',
]

const getRootAncestor = nodeId => {
  let current = nodeMap.value.get(nodeId)
  if (!current) return null
  while (current.relatedPathId) {
    current = nodeMap.value.get(current.relatedPathId)
    if (!current) return null
  }
  return current
}

const getNodeColorClass = node => {
  if (!node) return 'branch-color-default'
  const root = getRootAncestor(node.id)
  // Tambahkan .value di sini
  return root ? rootColorMap.value.get(root.id) : 'branch-color-default'
}

const rootColorMap = computed(() => {
  const map = new Map()
  const rootNodes = pathwayData.value.data.filter(p => p.level === 1)
  rootNodes.forEach((node, index) => {
    map.set(node.id, branchColors[index % branchColors.length])
  })
  return map
})

// Computed property to check if isFinal should be forced (auto-selected and disabled)
const shouldForceFinalClassification = computed(() => {
  // Only apply forcing logic on Breast Cancer page
  if (route.meta.title !== 'Breast Cancer') {
    return false
  }

  if (modalMode.value === 'add') {
    // For add mode: check if adding to Other Biomarkers parent or at level 5
    const parentNode = modalContext.value.parentId
      ? pathwayData.value.data.find(p => p.id === modalContext.value.parentId)
      : null

    const isOtherBiomarkers = parentNode?.name === 'Other Biomarkers'
    const isLevel5 = modalContext.value.level === 5

    return isOtherBiomarkers || isLevel5
  } else if (modalMode.value === 'edit') {
    // For edit mode: check if the node being edited is "Other Biomarkers" or at level 5
    const currentNode = editingNodeId.value
      ? pathwayData.value.data.find(p => p.id === editingNodeId.value)
      : null

    const isOtherBiomarkers = currentNode?.name === 'Other Biomarkers'
    const isLevel5 = currentNode?.level === 5

    return isOtherBiomarkers || isLevel5
  }

  return false
})

const selectedLevels = ref([])
const selectedBranches = ref([])

const levelOptions = computed(() => {
  if (!pathwayData.value.data) return []
  const levels = new Set(pathwayData.value.data.map(p => p.level))
  return Array.from(levels)
    .sort((a, b) => a - b)
    .map(level => ({ value: level, label: `Level ${level}` }))
})

const branchOptions = computed(() => {
  if (!pathwayData.value.data) return []
  return pathwayData.value.data
    .filter(p => p.level === 1)
    .map(branch => ({ value: branch.id, label: branch.name }))
})

const nodeMap = computed(() => new Map(pathwayData.value.data.map(node => [node.id, node])))

// Helper to check if an item belongs to a selected branch
const isItemInSelectedBranch = item => {
  if (!selectedBranches.value.length) return true
  const root = getRootAncestor(item.id)
  return root && selectedBranches.value.includes(root.id)
}

// This will contain ONLY the nodes that should be rendered as boxes.
const nodesToRender = computed(() => {
  let items = pathwayData.value.data.filter(isItemInSelectedBranch)

  if (selectedLevels.value.length > 0) {
    items = items.filter(item => selectedLevels.value.includes(item.level))
  }
  return items
})

const maxLevel = computed(() => {
  const data = pathwayData.value.data
  if (!data || data.length === 0) return 0
  return Math.max(...data.map(p => p.level))
})

const levelsToShow = computed(() => {
  // If no level filter is applied, show all levels
  if (selectedLevels.value.length === 0) {
    const levels = []
    for (let i = 1; i <= maxLevel.value + 1; i++) {
      levels.push(i)
    }
    return levels
  }

  // If level filter is applied, show only selected levels
  return [...selectedLevels.value].sort((a, b) => a - b)
})

const getPotentialParents = level => {
  if (level === 1) return [{ id: 'root', Drugs: [] }]

  if (selectedLevels.value.length === 0 || selectedLevels.value.includes(level)) {
    // Show ALL potential parents from previous level (non-final nodes)
    // Get all non-final nodes from the previous level that match branch filter
    const allPotentialParents = pathwayData.value.data.filter(
      p => p.level === level - 1 && (!p.Drugs || p.Drugs.length === 0),
    )

    return allPotentialParents.filter(isItemInSelectedBranch)
  }

  // If level filter is applied but this level is not selected, don't show any parents
  return []
}

const getChildrenOf = (parentId, level) => {
  const parentIdToCompare = parentId === 'root' ? null : parentId
  return nodesToRender.value.filter(p => p.level === level && p.relatedPathId === parentIdToCompare)
}
const getLineage = nodeId => {
  let lineage = []
  let current = nodeMap.value.get(nodeId)
  while (current?.relatedPathId) {
    current = nodeMap.value.get(current.relatedPathId)
    if (current) lineage.unshift(current)
  }
  return lineage
}

const resetForm = () => {
  Object.assign(formState, initialFormState)
  editingNodeId.value = null
  modalMode.value = 'add'
}

const openAddModal = parent => {
  resetForm()
  const isRoot = !parent || parent.id === 'root'
  modalContext.value = {
    parentId: isRoot ? null : parent.id,
    level: isRoot ? 1 : parent.level + 1,
    lineage: isRoot ? [] : getLineage(parent.id).concat(parent),
  }

  // Auto-select final classification for Other Biomarkers and level 5 (only on Breast Cancer page)
  if (route.meta.title === 'Breast Cancer') {
    const parentNode = parent && parent.id !== 'root' ? parent : null
    const isOtherBiomarkers = parentNode?.name === 'Other Biomarkers'
    const isLevel5 = modalContext.value.level === 5

    if (isOtherBiomarkers || isLevel5) {
      formState.isFinal = true
    }
  }

  // Reset image file list
  imageFileList.value = []
  // Set original form state for change tracking
  originalFormState.value = { ...initialFormState, imageFileList: [] }
  isModalVisible.value = true
}

const shouldShowAddButton = parent => {
  // Basic condition: root or non-final classification
  const basicCondition = parent.id === 'root' || !parent.Drugs || parent.Drugs.length === 0

  if (!basicCondition) return false

  // Special rules for Breast Cancer
  if (route.meta.title === 'Breast Cancer') {
    // For root, don't show add button
    if (parent.id === 'root') return false

    // For actual nodes, check level and name
    if (parent.level <= 2) {
      // Allow add button only for "Other Biomarker" node
      return parent.name === 'Other Biomarkers'
    }

    // For levels above 3, show normally
    return true
  }

  // For other cancer types, use basic condition
  return basicCondition
}

const findNodeById = nodeId => {
  return pathwayData.value.data.find(node => node.id === nodeId)
}

const openEditModal = nodeOrId => {
  // If we receive an ID, find the current node data; otherwise use the node directly
  const nodeId = typeof nodeOrId === 'object' ? nodeOrId.id : nodeOrId
  const node = findNodeById(nodeId)

  if (!node) {
    notification.error({
      message: 'Error',
      description: 'Could not find the selected pathway data.',
    })
    return
  }

  modalMode.value = 'edit'
  editingNodeId.value = node.id

  const editFormState = {
    name: node.name,
    code: node.code,
    detailInformation: node.detailInformation || '',
    isFinal: node.Drugs.length ? true : false,
    nonClickable: node.nonClickable || '',
    urlToStudy: node.urlToStudy || '',
    drugIds: node.Drugs ? node.Drugs.map(drug => drug.id) : [],
  }

  formState.name = editFormState.name
  formState.code = editFormState.code
  formState.detailInformation = editFormState.detailInformation
  formState.isFinal = editFormState.isFinal
  formState.nonClickable = editFormState.nonClickable
  formState.urlToStudy = editFormState.urlToStudy
  formState.drugIds = [...editFormState.drugIds]

  // Force final classification for Other Biomarkers and level 5 nodes (only on Breast Cancer page)
  if (route.meta.title === 'Breast Cancer') {
    const isOtherBiomarkers = node.name === 'Other Biomarkers'
    const isLevel5 = node.level === 5

    if (isOtherBiomarkers || isLevel5) {
      formState.isFinal = true
    }
  }

  // Handle pathway image for edit mode
  const currentImageFileList =
    node.photo && node.photoUrl
      ? [
          {
            uid: '-1',
            name: node.photo,
            status: 'done',
            url: node.photoUrl,
            thumbUrl: node.photoUrl,
            type: 'image/png',
            response: {
              url: node.photoUrl,
            },
            crossOrigin: 'anonymous',
          },
        ]
      : []

  imageFileList.value = currentImageFileList

  // Set original form state for change tracking
  originalFormState.value = { ...editFormState, imageFileList: [...currentImageFileList] }

  modalContext.value = {
    lineage: getLineage(node.id),
  }
  isModalVisible.value = true
}

const handleSave = async () => {
  try {
    // Validate form fields first
    await formRef.value.validate()

    // Custom validation for drugs when isFinal is true
    if (formState.isFinal && (!formState.drugIds || formState.drugIds.length === 0)) {
      notification.error({
        message: 'Validation Error',
        description: 'Please select at least one drug for final classification!',
      })
      return
    }

    if (modalMode.value === 'add') {
      // Debug logging to check values
      console.log('Modal context:', modalContext.value)
      console.log('Oncology ID:', oncologyId.value)
      console.log('Level:', modalContext.value.level)

      const formData = new FormData()
      formData.append('name', formState.name)
      formData.append('code', formState.code)
      formData.append('detailInformation', formState.detailInformation)
      formData.append('isFinal', formState.isFinal)
      formData.append('nonClickable', formState.nonClickable)
      formData.append('urlToStudy', formState.urlToStudy)
      formData.append('drugIds', JSON.stringify(formState.drugIds))
      formData.append('oncologyId', Number(oncologyId.value))

      // Validate and append level
      const level = Number(modalContext.value.level)
      if (!isNaN(level) && level > 0) {
        formData.append('level', level)
      } else {
        formData.append('level', 1) // Default to level 1
      }

      // Only append relatedPathId if it's not null
      if (modalContext.value.parentId !== null) {
        formData.append('relatedPathId', modalContext.value.parentId)
      }

      // Add pathway image if selected
      if (imageFileList.value.length > 0 && imageFileList.value[0].originFileObj) {
        formData.append('pathwayImage', imageFileList.value[0].originFileObj)
      }

      await PathwayAPI.createPathway(formData)
      notification.success({
        message: 'Success',
        description: 'New classification has been added.',
      })
    } else {
      // Mode 'edit'
      const formData = new FormData()
      formData.append('name', formState.name)
      formData.append('code', formState.code)
      formData.append('detailInformation', formState.detailInformation)
      formData.append('isFinal', formState.isFinal)
      formData.append('nonClickable', formState.nonClickable)
      formData.append('urlToStudy', formState.urlToStudy)
      formData.append('drugIds', JSON.stringify(formState.drugIds))

      // Handle pathway image changes
      const hasNewImage = imageFileList.value.length > 0 && imageFileList.value[0].originFileObj
      const hadOriginalImage =
        originalFormState.value.imageFileList && originalFormState.value.imageFileList.length > 0
      const imageWasDeleted = hadOriginalImage && imageFileList.value.length === 0

      if (hasNewImage) {
        // User uploaded a new image
        formData.append('pathwayImage', imageFileList.value[0].originFileObj)
      } else if (imageWasDeleted) {
        // User deleted the existing image - inform backend to remove it
        formData.append('deleteImage', 'true')
      }

      await PathwayAPI.updatePathway(editingNodeId.value, formData)
      notification.success({ message: 'Success', description: 'Classification has been updated.' })
    }

    await fetchPathways() // Fetch fresh data first
    closeModal() // Then close modal
  } catch (error) {
    console.error('Save error:', error)

    let errorMessage = 'An unexpected error occurred while saving.'

    if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error
    } else if (error.message) {
      errorMessage = error.message
    }

    notification.error({
      message: 'Save Failed',
      description: errorMessage,
    })
  }
}

const handleCancel = () => {
  if (hasFormChanges.value) {
    cancelConfirmVisible.value = true
  } else {
    closeModal()
  }
}

const handlePreview = async file => {
  if (!file.url && !file.preview) {
    file.preview = await getBase64(file.originFileObj)
  }
  previewImage.value = file.url || file.preview
  previewVisible.value = true
  previewTitle.value =
    file.name || (file.url ? file.url.substring(file.url.lastIndexOf('/') + 1) : 'Image Preview')
}

const handlePreviewCancel = () => {
  previewVisible.value = false
}

const beforeUpload = file => {
  const isImage = file.type.startsWith('image/')
  if (!isImage) {
    notification.error({
      message: 'Upload Error',
      description: 'You can only upload image files!',
    })
    return false
  }

  const isLt5M = file.size / 1024 / 1024 < 5
  if (!isLt5M) {
    return false
  }

  return false // Prevent automatic upload, we handle it manually
}

const handleUploadChange = info => {
  const { fileList } = info

  // Filter out files that don't pass validation
  const validFiles = fileList.filter(file => {
    if (file.status === 'uploading' || file.status === 'done') {
      return true
    }

    // Check if it's a new file being added
    if (!file.status) {
      const isImage = file.type.startsWith('image/')
      const isLt5M = file.size / 1024 / 1024 < 5

      if (!isImage) {
        notification.error({
          message: 'Upload Error',
          description: 'You can only upload image files!',
        })
        return false
      }

      if (!isLt5M) {
        notification.error({
          message: 'Upload Error',
          description: 'Image must be smaller than 5MB!',
        })
        return false
      }
    }

    return true
  })

  // Update the file list only with valid files
  imageFileList.value = validFiles
}

const getBase64 = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = error => reject(error)
  })
}

const confirmCancel = () => {
  cancelConfirmVisible.value = false
  closeModal()
}

const closeModal = () => {
  isModalVisible.value = false
  // Reset form state
  Object.assign(formState, initialFormState)
  // Reset image file list
  imageFileList.value = []
  originalFormState.value = { ...initialFormState, imageFileList: [] }
  // Clear form validation errors
  if (formRef.value) {
    formRef.value.clearValidate()
  }
}

const getAllDescendantIds = nodeId => {
  let descendantIds = []
  const children = pathwayData.value.data.filter(p => p.relatedPathId === nodeId)
  for (const child of children) {
    descendantIds.push(child.id)
    descendantIds = descendantIds.concat(getAllDescendantIds(child.id))
  }
  return descendantIds
}
const handleToggleDelete = () => {
  toggleDelete.value = true
}
const handleDelete = async () => {
  await PathwayAPI.deletePathway(editingNodeId.value)
  notification.success({
    message: 'Success',
    description: 'Classification has been deleted.',
  })
  handleCancel()
  await fetchPathways()
  toggleDelete.value = false
  isModalVisible.value = false
}
watch(
  () => route.meta.title,
  (newTitle, oldTitle) => {
    // Jika judulnya berubah, panggil ulang fetchPathways
    if (newTitle !== oldTitle) {
      // console.log(`Route changed to: ${newTitle}, fetching new data...`)
      fetchPathways()
    }
  },
)
watch(
  () => formState.isFinal,
  isFinal => {
    if (!isFinal) {
      formState.urlToStudy = ''
      formState.drugIds = []
      formState.nonClickable = ''
    }
  },
)
const isLoading = ref(false)
const oncologyId = ref(0)
const fetchPathways = async () => {
  try {
    isLoading.value = true
    // console.log(route.meta.title, '??')

    switch (route.meta.title) {
      case 'Breast Cancer':
        oncologyId.value = 1
        break
      case 'Tyroid Cancer':
        oncologyId.value = 2
        break
      case 'Miscellaneous Cancer':
        oncologyId.value = 3
        break
      case 'Skin Cancer':
        oncologyId.value = 4
        break
      case 'Sarcoma Cancer':
        oncologyId.value = 5
        break
      case 'Lymphoma Cancer':
        oncologyId.value = 6
        break
      case 'Salivary Gland Cancer':
        oncologyId.value = 7
        break
      case 'Head & Neck':
        oncologyId.value = 8
        break

      default:
        oncologyId.value = 1
        break
    }
    // console.log(oncologyId.value, '??')
    const response = await PathwayAPI.getPathwaysByOncology(oncologyId.value)
    pathwayData.value.data = response.data
    // console.log(pathwayData.value.data)
  } catch (error) {
    console.log(error)
    notification.error({
      message: 'Failed to Fetch Data',
      description: 'Could not retrieve pathway data from the server.',
    })
  } finally {
    isLoading.value = false
  }
}

const fetchAllDrugs = async () => {
  try {
    const response = await DrugAPI.getAllDrugs()
    allDrugs.value = response.data // Mengisi state dengan data dari API
  } catch (error) {
    notification.error({
      message: 'Failed to Fetch Drugs',
      description: 'Could not retrieve the list of drugs from the server.',
    })
  }
}
onMounted(() => {
  Promise.all([fetchPathways(), fetchAllDrugs()])
})
</script>

<style scoped>
.pathway-layout {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.level-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.level-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}
.level-label {
  font-weight: 600;
  font-size: 18px;
  color: #333;
}
.level-body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  padding-left: 30px;
  border-left: 2px dashed #e0e0e0;
}
.lineage-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}
.lineage-parent {
  display: flex;
  align-items: center;
}
.lineage-path {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.lineage-children {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  background-color: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  flex-wrap: wrap;
  min-height: 54px;
}

.pathway-group {
  display: inline-flex;
  align-items: center;
}
.pathway-item {
  display: flex;
  align-items: center;
  border: 1px solid #d9d9d9;
  background-color: white !important;
  border-radius: 16px;
  padding: 4px 12px 4px 4px;
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.3s;
}
.pathway-item:hover {
  background-color: #e6f7ff;
}
.pathway-item.mini {
  padding: 2px 8px 2px 2px;
  background-color: #ebebeb;
}
.pathway-item.mini .item-badge {
  min-width: 20px;
  height: 20px;
  line-height: 20px;
  font-size: 10px;
  margin-right: 6px;
  border-radius: 10px;
  padding: 0 3px;
}

.item-badge {
  display: inline-block;
  color: white;
  border-radius: 12px;
  min-width: 24px;
  height: 24px;
  line-height: 24px;
  text-align: center;
  font-weight: bold;
  margin-right: 8px;
  font-size: 12px;
  flex-shrink: 0;
  padding: 0 4px;
  box-sizing: border-box;
}
.item-name {
  color: #262626;
}
.connector {
  font-size: 20px;
  color: #bfbfbf;
  margin: 0 8px;
}
.connector.mini-arrow {
  font-size: 16px;
  margin: 0 4px;
}
.modal-lineage {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}
.modal-footer {
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.add-classification-btn {
  color: #8c8c8c;
}
.badge-red {
  background-color: #f5222d;
}
.badge-dark-red {
  background-color: #a8071a;
}
.badge-pink {
  background-color: #eb2f96;
}
.badge-purple {
  background-color: #722ed1;
}
.badge-orange {
  background-color: #fa8c16;
}
.badge-green {
  background-color: #52c41a;
}
.badge-blue {
  background-color: #1890ff;
}
.badge-grey {
  background-color: #8c8c8c;
}
/* Tampilan Jejak Parent yang Baru */
.lineage-badge {
  color: white;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  font-weight: bold;
  font-size: 14px;
  flex-shrink: 0;
}

/* Tampilan Node Utama (Modifikasi) */
.pathway-item {
  display: flex;
  align-items: center;
  background-color: white;
  border: 1.5px solid; /* Warna diatur oleh kelas cabang */
  border-radius: 20px;
  padding: 4px 16px 4px 6px;
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.3s;
}

/* Tombol Add Classification Baru */
.add-classification-text-btn {
  color: #1890ff;
  padding: 0;
  font-size: 14px;
}
.add-classification-text-btn i {
  margin-right: 4px;
}

/* Kelas Warna per Cabang (Baru) */
.branch-color-1,
.pathway-item.branch-color-1 .item-badge,
.lineage-badge.branch-color-1 {
  background-color: #c34f6b;
}
.branch-color-2,
.pathway-item.branch-color-2 .item-badge,
.lineage-badge.branch-color-2 {
  background-color: #e06e52;
}
.branch-color-3,
.pathway-item.branch-color-3 .item-badge,
.lineage-badge.branch-color-3 {
  background-color: #531dab;
}
.branch-color-4,
.pathway-item.branch-color-4 .item-badge,
.lineage-badge.branch-color-4 {
  background-color: #08979c;
}
.branch-color-5,
.pathway-item.branch-color-5 .item-badge,
.lineage-badge.branch-color-5 {
  background-color: #c41d7f;
}
.branch-color-default,
.pathway-item.branch-color-default .item-badge,
.lineage-badge.branch-color-default {
  background-color: #8c8c8c;
}

/* Mengaplikasikan warna ke border node utama (Baru) */
.pathway-item.branch-color-1 {
  border-color: #c34f6b;
}
.pathway-item.branch-color-2 {
  border-color: #e06e52;
}
.pathway-item.branch-color-3 {
  border-color: #d3adf7;
}
.pathway-item.branch-color-4 {
  border-color: #b5f5ec;
}
.pathway-item.branch-color-5 {
  border-color: #ffd6e7;
}
.pathway-item.branch-color-default {
  border-color: #d9d9d9;
}

/* Custom placeholder color styling */
:deep(.ant-input::placeholder) {
  color: #9ca3af !important;
}

:deep(.ant-select .ant-select-selection-placeholder) {
  color: #9ca3af !important;
}

:deep(.ant-select-multiple .ant-select-selection-placeholder) {
  color: #9ca3af !important;
}
</style>
